import * as THREE from "three";
import type { SuntraceQuality } from "./types";

export type SuntraceEnvironmentMaterialIdentity =
  | "rock"
  | "vegetation"
  | "concrete"
  | "painted-metal";

export type SuntraceEnvironmentMaterialShaderContract = {
  vertexPatch: string;
  fragmentPatch: string;
};

export type SuntraceEnvironmentMaterialController = {
  readonly weatheringStrength: number;
  setQuality: (quality: SuntraceQuality) => void;
  dispose: () => void;
};

export type SuntraceEnvironmentMaterialResponseCollection = {
  readonly weatheringEnabled: boolean;
  register: (
    material: THREE.MeshStandardMaterial,
    identity: SuntraceEnvironmentMaterialIdentity,
  ) => SuntraceEnvironmentMaterialController;
  release: (material: THREE.Material) => void;
  setQuality: (quality: SuntraceQuality) => void;
  dispose: () => void;
};

const materialResponses: Record<SuntraceEnvironmentMaterialIdentity, {
  saltTint: string;
  weatheredRoughness: string;
  colorStrength: string;
}> = {
  rock: {
    saltTint: "vec3( 0.74, 0.84, 0.83 )",
    weatheredRoughness: "0.94",
    colorStrength: "0.34",
  },
  vegetation: {
    saltTint: "vec3( 0.77, 0.88, 0.76 )",
    weatheredRoughness: "0.81",
    colorStrength: "0.18",
  },
  concrete: {
    saltTint: "vec3( 0.78, 0.84, 0.84 )",
    weatheredRoughness: "0.91",
    colorStrength: "0.28",
  },
  "painted-metal": {
    saltTint: "vec3( 0.83, 0.9, 0.89 )",
    weatheredRoughness: "0.7",
    colorStrength: "0.2",
  },
};

const shaderPreamble = `
uniform float uSuntraceWeathering;
varying vec3 vSuntraceWorldPosition;
varying vec3 vSuntraceWorldNormal;
`;

const worldPositionPatch = `
vec4 suntraceWorldPosition = vec4( transformed, 1.0 );
#ifdef USE_BATCHING
suntraceWorldPosition = batchingMatrix * suntraceWorldPosition;
#endif
#ifdef USE_INSTANCING
suntraceWorldPosition = instanceMatrix * suntraceWorldPosition;
#endif
suntraceWorldPosition = modelMatrix * suntraceWorldPosition;
vSuntraceWorldPosition = suntraceWorldPosition.xyz;
`;

const worldNormalPatch = `
vSuntraceWorldNormal = normalize( transpose( mat3( viewMatrix ) ) * transformedNormal );
`;

const getFragmentPatch = (
  identity: SuntraceEnvironmentMaterialIdentity,
): string => {
  const response = materialResponses[identity];
  return `
float suntraceSeaLevelExposure = 1.0 - smoothstep( 5.0, 24.0, abs( vSuntraceWorldPosition.y ) );
float suntraceUpwardExposure = pow( clamp( vSuntraceWorldNormal.y * 0.5 + 0.5, 0.0, 1.0 ), 2.0 );
float suntraceCoastalField = sin( vSuntraceWorldPosition.x * 0.071 + sin( vSuntraceWorldPosition.z * 0.043 ) * 1.9 );
float suntraceMacroWeathering = suntraceCoastalField * 0.5 + 0.5;
float suntraceMicroCoordinate = dot( vSuntraceWorldPosition, vec3( 2.7, 0.0, 3.1 ) );
float suntraceMicroFootprint = fwidth( suntraceMicroCoordinate );
float suntraceMicroDetail = sin( suntraceMicroCoordinate ) * 0.5 + 0.5;
float suntraceFilteredMicrodetail = suntraceMicroDetail * ( 1.0 - smoothstep( 0.015, 0.08, suntraceMicroFootprint ) );
float suntraceSaltExposure = clamp( suntraceSeaLevelExposure * 0.48 + suntraceUpwardExposure * 0.32 + suntraceMacroWeathering * 0.14 + suntraceFilteredMicrodetail * 0.06, 0.0, 1.0 );
float suntraceWeatheredRoughness = mix( roughnessFactor, ${response.weatheredRoughness}, suntraceSaltExposure * uSuntraceWeathering );
diffuseColor.rgb = mix( diffuseColor.rgb, diffuseColor.rgb * ${response.saltTint}, suntraceSaltExposure * uSuntraceWeathering * ${response.colorStrength} );
roughnessFactor = clamp( suntraceWeatheredRoughness, 0.04, 1.0 );
`;
};

const injectAfter = (source: string, marker: string, patch: string): string => {
  const index = source.indexOf(marker);
  if (index < 0) {
    throw new Error(`SUNTRACE shader marker is unavailable: ${marker}`);
  }
  const insertionPoint = index + marker.length;
  return `${source.slice(0, insertionPoint)}${patch}${source.slice(insertionPoint)}`;
};

const injectWorldPositionAndNormal = (vertexShader: string): string =>
  injectAfter(
    injectAfter(
      injectAfter(vertexShader, "#include <common>", shaderPreamble),
      "#include <defaultnormal_vertex>",
      worldNormalPatch,
    ),
    "#include <displacementmap_vertex>",
    worldPositionPatch,
  );

const injectCausalWeathering = (
  fragmentShader: string,
  identity: SuntraceEnvironmentMaterialIdentity,
): string =>
  injectAfter(
    injectAfter(fragmentShader, "#include <common>", shaderPreamble),
    "#include <roughnessmap_fragment>",
    getFragmentPatch(identity),
  );

const getWeatheringStrength = (quality: SuntraceQuality): number =>
  quality === "high" ? 1 : quality === "medium" ? 0.58 : 0;

export const getSuntraceEnvironmentMaterialShaderContract = (
  identity: SuntraceEnvironmentMaterialIdentity,
): SuntraceEnvironmentMaterialShaderContract => ({
  vertexPatch: `${worldNormalPatch}${worldPositionPatch}`,
  fragmentPatch: getFragmentPatch(identity),
});

export const applySuntraceEnvironmentMaterialResponse = (
  material: THREE.MeshStandardMaterial,
  identity: SuntraceEnvironmentMaterialIdentity,
): SuntraceEnvironmentMaterialController => {
  const uniforms = { uSuntraceWeathering: { value: 0 } };
  const onBeforeCompile: THREE.Material["onBeforeCompile"] = Reflect.get(
    material,
    "onBeforeCompile",
  );
  const customProgramCacheKey: THREE.Material["customProgramCacheKey"] = Reflect.get(
    material,
    "customProgramCacheKey",
  );
  let weatheringStrength = 0;
  let disposed = false;
  material.onBeforeCompile = (shader, renderer) => {
    onBeforeCompile.call(material, shader, renderer);
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = injectWorldPositionAndNormal(shader.vertexShader);
    shader.fragmentShader = injectCausalWeathering(shader.fragmentShader, identity);
  };
  material.customProgramCacheKey = () =>
    `${customProgramCacheKey.call(material)}|suntrace-environment-${identity}-v2`;
  material.needsUpdate = true;
  return {
    get weatheringStrength() {
      return weatheringStrength;
    },
    setQuality(quality) {
      if (disposed) {
        return;
      }
      weatheringStrength = getWeatheringStrength(quality);
      uniforms.uSuntraceWeathering.value = weatheringStrength;
    },
    dispose() {
      if (disposed) {
        return;
      }
      disposed = true;
      material.onBeforeCompile = onBeforeCompile;
      material.customProgramCacheKey = customProgramCacheKey;
      material.needsUpdate = true;
    },
  };
};

export const createSuntraceEnvironmentMaterialResponseCollection = (): SuntraceEnvironmentMaterialResponseCollection => {
  const controllers = new Map<
    THREE.Material,
    SuntraceEnvironmentMaterialController
  >();
  return {
    get weatheringEnabled() {
      return [...controllers.values()].some(
        ({ weatheringStrength }) => weatheringStrength > 0,
      );
    },
    register(material, identity) {
      controllers.get(material)?.dispose();
      const controller = applySuntraceEnvironmentMaterialResponse(material, identity);
      controllers.set(material, controller);
      return controller;
    },
    release(material) {
      controllers.get(material)?.dispose();
      controllers.delete(material);
    },
    setQuality(quality) {
      for (const controller of controllers.values()) {
        controller.setQuality(quality);
      }
    },
    dispose() {
      for (const controller of controllers.values()) {
        controller.dispose();
      }
      controllers.clear();
    },
  };
};
